/**
 * @file card.component.ts
 * Composant Angular générique pour une carte TCG à double face.
 *
 * Fonctionnalités :
 * - Flip 3D GSAP (recto ↔ verso) au clic
 * - Tilt parallaxe 3D au survol (rotationX/Y + déplacement des éléments)
 * - Gestion des effets selon la rareté :
 *   ┌─────────────┬───────────┬──────────┬──────────┬──────────────┐
 *   │  Rareté     │ Particules│ Paillettes│ Étincelles│ Foudre canvas│
 *   ├─────────────┼───────────┼──────────┼──────────┼──────────────┤
 *   │ common      │ ✓         │ ✗        │ ✗        │ ✗            │
 *   │ rare        │ ✓         │ ✗        │ ✗        │ ✗            │
 *   │ epic        │ ✓         │ ✗        │ ✓        │ ✗            │
 *   │ legendary   │ ✓         │ ✓        │ ✓        │ ✗            │
 *   │ collector   │ ✓         │ ✓        │ ✓        │ ✓            │
 *   └─────────────┴───────────┴──────────┴──────────┴──────────────┘
 *
 * Usage :
 * ```html
 * <app-card
 *   rarity="legendary"
 *   name="WORLDS 2025"
 *   [score]="100"
 *   [rating]="4"
 *   author="@Mimo"
 *   image="https://..."
 *   [initiallyFlipped]="true"
 * />
 * ```
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SecurityContext,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';


import gsap from 'gsap';

// force3D permanent : évite que GSAP repasse en translate() 2D à la fin de chaque tween
// (perte puis reprise de la promotion de calque = re-raster + saut visible d'une frame).
gsap.defaults({ force3D: true });

import { environment } from '@/environments/environment';
import { CardRarity, RC, PEPITE, PEPITE_BLACK } from './card.model';
import { CardParticleService } from './effects/card-particle.service';
import { CardLightningEffect } from './effects/card-lightning.effect';
import { CardVisibilityService } from './effects/card-visibility.service';
import { CardSoundService } from '@services/card-game/card-sound.service';
import {NgClass} from '@angular/common';

@Component({
  selector: 'app-card',
  standalone: true,
	imports: [
		NgClass
	],
  templateUrl: './card.component.html',
  styleUrl: './card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // CardParticleService est fourni localement : chaque carte a son propre état de particules.
  providers: [CardParticleService],
})
export class CardComponent implements AfterViewInit, OnChanges, OnDestroy {

  // ── Inputs ────────────────────────────────────────────────────────────────

  /** Identifiant de la rareté — détermine couleurs, effets et label. */
  @Input() rarity: CardRarity = 'COMMON';

  /** Nom du joueur / titre de la carte. */
  @Input() name = '';

  /** Nom de l'auteur de la carte. */
  @Input() author = '';

  /** Score affiché en grand (ex : 100). */
  @Input() score = 0;

  /** Nombre d'étoiles pleines sur 4. */
  @Input() rating = 0;

  /** Nom de fichier / clé de l'image recto de la carte, stockée sur MinIO. */
  @Input() image = '';

  /** URL de base MinIO — préfixée à `image` pour la face recto (le verso reste un asset local). */
  minioBaseUrl = environment.minioBaseUrl;

  /** Si `true`, la carte démarre côté dos (état par défaut lors de l'ouverture de booster). */
  @Input() initiallyFlipped = false;

  @Input() backOfCard = '';
  @Input() extensionId = '';
  /**
   * Si `false`, désactive le tilt souris + halo hover interne (GSAP) et
   * réduit le nombre de particules/étincelles.
   *
   * Deux cas d'usage bien distincts s'en servent :
   * - Affichages statiques en grand format (modal détail) : voir aussi
   *   {@link staticDisplay}, qui coupe en plus le halo au survol CSS — le
   *   rendu 3D + blur(70px) coûterait trop cher au scale.
   * - Cartes de l'ouverture de booster (`CardsLayerComponent`) : le survol/
   *   zoom y est déjà géré par le parent (`enableHoverZoom()`), on évite
   *   juste un second système de tilt concurrent — le halo au survol CSS,
   *   lui, doit rester actif (voir `staticDisplay`, resté à `false` ici).
   */
  @Input() interactive = true;

  /**
   * Si `true`, coupe en plus le halo au survol (CSS) et le zoom interne de
   * l'image — réservé aux affichages statiques en grand format (modal
   * détail agrandie ×1.3/×3) où ces effets coûtent cher à repeindre.
   * Sans lien avec `interactive` : les cartes de booster passent
   * `interactive=false` mais gardent `staticDisplay=false` pour conserver
   * le halo au survol.
   */
  @Input() staticDisplay = false;

  /**
   * Si `true`, met en pause les effets perpétuels (particules, foudre) sans
   * les désactiver définitivement — ex : grille d'inventaire pendant que la
   * modal détail est ouverte par-dessus. Reprennent dès repassage à `false`
   * (si la carte est toujours visible à l'écran).
   */
  @Input() paused = false;

  /**
   * Si `true`, affiche un loader par-dessus l'image tant qu'elle n'a pas fini
   * de charger — utile sur les grilles (profil, marketplace) où l'image vient
   * de MinIO. Désactivé par défaut pour ne pas impacter l'ouverture de
   * booster (cascade de cartes déjà chorégraphiée, pas de flash de loader).
   */
  @Input() showLoader = false;

  /** Vrai une fois l'image recto chargée (ou en erreur) — pilote le loader. */
  readonly imageLoaded = signal(false);

  onImageLoad(): void {
    this.imageLoaded.set(true);
  }

  // ── ViewChild refs ────────────────────────────────────────────────────────

  @ViewChild('scene')              sceneRef!:              ElementRef<HTMLElement>;
  @ViewChild('cardWrap')           cardWrapRef!:           ElementRef<HTMLElement>;
  @ViewChild('cardFront')          cardFrontRef!:          ElementRef<HTMLElement>;
  @ViewChild('particlesContainer') particlesContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('scoreEl')            scoreElRef!:            ElementRef<HTMLElement>;
  @ViewChild('logoEl')             logoElRef!:             ElementRef<HTMLElement>;
  @ViewChild('nameEl')             nameElRef!:             ElementRef<HTMLElement>;
  @ViewChild('starsEl')            starsElRef!:            ElementRef<HTMLElement>;
  @ViewChild('cornerTL')           cornerTLRef!:           ElementRef<HTMLElement>;
  @ViewChild('cornerTR')           cornerTRRef!:           ElementRef<HTMLElement>;
  @ViewChild('cornerBL')           cornerBLRef!:           ElementRef<HTMLElement>;
  @ViewChild('cornerBR')           cornerBRRef!:           ElementRef<HTMLElement>;

  // Optionnels : présents seulement si hasGlitterEffect / hasSparkleEffect / hasLightningEffect
  @ViewChild('particleFromBottom') particleFromBottomRef?: ElementRef<HTMLElement>;
  @ViewChild('randomLightLayer')   randomLightLayerRef?:   ElementRef<HTMLElement>;
  @ViewChild('lightningCanvas')    lightningCanvasRef?:    ElementRef<HTMLCanvasElement>;

  // ── Template properties ───────────────────────────────────────────────────

  /** SVG PEPITE sanitisé pour le binding [innerHTML]. */
  readonly pepiteSafe: SafeHtml;

  /** SVG PEPITE_BLACK sanitisé pour le binding [innerHTML]. */
  readonly pepiteBlackSafe: SafeHtml;

  /** Tableau d'indices pour `@for` des étoiles pleines. */
  get filledStars(): number[] { return Array.from({ length: this.rating }); }

  /** Tableau d'indices pour `@for` des étoiles vides. */
  get emptyStars(): number[] { return Array.from({ length: Math.max(0, 5 - this.rating) }); }

  /** Vrai si cette rareté affiche des paillettes montantes. */
  get hasGlitterEffect(): boolean {
    return this.rarity === 'COLLECTOR' || this.rarity === 'LEGENDARY';
  }

  /** Vrai si cette rareté affiche des étincelles color-dodge. */
  get hasSparkleEffect(): boolean {
    return this.rarity === 'COLLECTOR' || this.rarity === 'LEGENDARY' || this.rarity === 'EPIC';
  }

  /** Vrai si cette rareté affiche de la foudre sur canvas. */
  get hasLightningEffect(): boolean {
    return this.rarity === 'COLLECTOR';
  }

  // ── État interne ──────────────────────────────────────────────────────────

  private isFlipped = false;
  private isAnimating = false;
  private effectsStarted = false;

  /** Vrai une fois la face recto atteinte (init directe ou fin de flip) — condition pour lancer les effets. */
  private frontVisible = false;
  /** Vrai tant que la carte intersecte le viewport (IntersectionObserver). */
  private isVisible = false;
  /** Vrai une fois ngAfterViewInit passé — garde contre les ngOnChanges prématurés. */
  private viewReady = false;

  /** Instance de l'effet de foudre canvas — non null pour collector uniquement. */
  private lightningEffect: CardLightningEffect | null = null;

  /** ID du requestAnimationFrame courant. */
  private animFrameId: number | null = null;

  private lastFrameTime = 0;

  // GSAP quickTo handles pour le tilt — créés au premier survol (cf. ensureGsapState)
  private setRotX!:  (v: number) => void;
  private setRotY!:  (v: number) => void;
  private setScale!: (v: number) => void;
  /** Parallaxe : 2 quickTo sur un proxy, qui pilotent tous les éléments de la face via quickSetter. */
  private setParX!:  (v: number) => void;
  private setParY!:  (v: number) => void;
  private readonly parallax = { x: 0, y: 0 };
  private gsapStateReady = false;

  /** Dernière position souris, appliquée au plus une fois par frame. */
  private pointerX = 0;
  private pointerY = 0;
  private pointerRaf = 0;

  constructor(
    private ngZone: NgZone,
    private particleService: CardParticleService,
    private visibilityService: CardVisibilityService,
    private soundService: CardSoundService,
    sanitizer: DomSanitizer,
  ) {
    this.pepiteSafe      = sanitizer.bypassSecurityTrustHtml(PEPITE);
    this.pepiteBlackSafe = sanitizer.bypassSecurityTrustHtml(PEPITE_BLACK);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.applyCssVars();
    // Tout GSAP (set, ticker, listeners) hors zone : le rAF récursif du ticker
    // hérite de la zone dans laquelle il a été planifié la première fois — créé
    // dans la zone Angular, il déclencherait un cycle de CD complet à chaque frame.
    this.ngZone.runOutsideAngular(() => {
      this.initFlipState();
      if (!this.initiallyFlipped) {
        this.frontVisible = true;
      }
      this.bindMouseEvents();
      this.observeVisibility();
    });
    this.viewReady = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady || !changes['paused']) return;
    this.ngZone.runOutsideAngular(() => this.syncEffectsState());
  }

  /**
   * Démarre/arrête les effets perpétuels en fonction de la visibilité réelle
   * de la carte — inutile d'animer des particules ou de faire tourner la
   * foudre canvas pour une carte hors écran dans une grille de plusieurs
   * dizaines de cartes. Passe par {@link CardVisibilityService} : un seul
   * `IntersectionObserver` partagé pour toute l'app plutôt qu'une instance
   * par carte.
   */
  private observeVisibility(): void {
    this.visibilityService.observe(this.sceneRef.nativeElement, (visible) => {
      this.isVisible = visible;
      this.syncEffectsState();
    });
  }

  /** Concilie visibilité + pause explicite + état de flip pour lancer/couper les effets. */
  private syncEffectsState(): void {
    if (!this.frontVisible) return;
    if (this.isVisible && !this.paused) {
      if (!this.effectsStarted) this.startEffects();
    } else {
      this.pauseEffects();
    }
  }

  /**
   * Coupe les boucles perpétuelles (particules, foudre) sans détruire le
   * composant. Les particules en vol sont tuées et remises dans leur pool.
   */
  private pauseEffects(): void {
    this.sceneRef.nativeElement.classList.remove('card-effects-active');
    // Tue tweens en vol + appels différés : sans ça, les anciennes chaînes de
    // respawn reprenaient au retour dans le viewport en plus des nouvelles.
    this.particleService.stopAll();
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    // Permet un redémarrage propre (spawns frais) à la prochaine reprise de visibilité.
    this.effectsStarted = false;
  }

  ngOnDestroy(): void {
    const scene = this.sceneRef?.nativeElement;
    if (scene) {
      this.visibilityService.unobserve(scene);
      scene.removeEventListener('click', this.onClick);
      scene.removeEventListener('mousemove', this.onMouseMove);
      scene.removeEventListener('mouseover', this.onMouseOver);
      scene.removeEventListener('mouseout', this.onMouseOut);
    }

    // Tue les tweens de particules et les appels différés en attente
    this.particleService.stopAll();

    if (this.pointerRaf) {
      cancelAnimationFrame(this.pointerRaf);
      this.pointerRaf = 0;
    }

    // Annule la boucle de foudre si active
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }

    // Libère l'effet de foudre canvas
    this.lightningEffect?.dispose();

    // Tue les tweens GSAP de la scène, du pivot et du proxy de parallaxe
    gsap.killTweensOf(this.parallax);
    if (scene) gsap.killTweensOf(scene);
    if (this.cardWrapRef?.nativeElement) gsap.killTweensOf(this.cardWrapRef.nativeElement);
    this.parallaxTargets().forEach(el => gsap.killTweensOf(el));
  }

  // ── Initialisation ────────────────────────────────────────────────────────

  /**
   * Applique les variables CSS de rareté directement sur .card-scene.
   * Permet au CSS de n'utiliser que `var(--xxx)` sans duplication de valeurs.
   */
  private applyCssVars(): void {
    const r   = RC[this.rarity] ?? RC['COMMON'];
    const el  = this.sceneRef.nativeElement;
    el.style.setProperty('--glow-bg',        r.glowBg);
    el.style.setProperty('--particle-color', r.particleColor);
    el.style.setProperty('--border-bg',      r.borderBg);
    el.style.setProperty('--inner-glow',     r.innerGlow);
    el.style.setProperty('--score-gradient', r.scoreGradient);
    el.style.setProperty('--hud-color',      r.c1);
  }


  /**
   * Positionne le pivot GSAP selon l'état initial de la carte.
   * rotationY=180 → dos visible ; rotationY=0 → face visible.
   */
  private initFlipState(): void {
    this.isFlipped = this.initiallyFlipped;
    gsap.set(this.cardWrapRef.nativeElement, {
      rotationY: this.isFlipped ? 180 : 0,
    });
  }

  /** Éléments de la face recto déplacés en parallaxe. */
  private parallaxTargets(): HTMLElement[] {
    return [
      this.scoreElRef, this.logoElRef, this.nameElRef, this.starsElRef,
      this.cornerTLRef, this.cornerTRRef, this.cornerBLRef, this.cornerBRRef,
    ]
      .map(ref => ref?.nativeElement as HTMLElement | undefined)
      .filter((el): el is HTMLElement => !!el);
  }

  /**
   * Crée les handles GSAP au premier survol seulement : inutile d'allouer ~20
   * tweens pour chaque carte d'une grille qui ne sera jamais survolée.
   * Rotation / scale : 3 `quickTo`. Parallaxe : 2 `quickTo` sur un proxy dont
   * l'`onUpdate` écrit x/y dans les 8 éléments via `quickSetter` (écriture
   * directe, sans tween par élément).
   */
  private ensureGsapState(): void {
    if (this.gsapStateReady) return;
    this.gsapStateReady = true;

    const wrap = this.cardWrapRef.nativeElement;
    this.setRotX  = gsap.quickTo(wrap, 'rotationX', { duration: 0.45, ease: 'power3.out' });
    this.setRotY  = gsap.quickTo(wrap, 'rotationY', { duration: 0.45, ease: 'power3.out' });
    this.setScale = gsap.quickTo(wrap, 'scale',     { duration: 0.45, ease: 'power3.out' });

    const targets = this.parallaxTargets();
    const setX = targets.map(el => gsap.quickSetter(el, 'x', 'px') as (v: number) => void);
    const setY = targets.map(el => gsap.quickSetter(el, 'y', 'px') as (v: number) => void);
    const apply = () => {
      for (let i = 0; i < targets.length; i++) {
        setX[i](this.parallax.x);
        setY[i](this.parallax.y);
      }
    };

    // X d'abord, Y ensuite : l'onUpdate de Y s'exécute après la mise à jour des deux axes.
    this.setParX = gsap.quickTo(this.parallax, 'x', { duration: 0.5, ease: 'power2.out' });
    this.setParY = gsap.quickTo(this.parallax, 'y', { duration: 0.5, ease: 'power2.out', onUpdate: apply });
  }

  /** Démarre les particules et effets de rareté — appelé une seule fois. */
  private startEffects(): void {
    if (this.effectsStarted) return;
    this.effectsStarted = true;
    this.sceneRef.nativeElement.classList.add('card-effects-active');
    this.particleService.active = true;
    this.initParticles();
    this.initRarityEffects();
  }

  /**
   * Lance les particules ambiantes génériques (toutes raretés).
   * 3 particules en décalage temporel pour éviter une apparition simultanée.
   */
  private initParticles(): void {
    const container = this.particlesContainerRef.nativeElement;
    const color     = (RC[this.rarity] ?? RC['COMMON']).particleColor;

    const count = this.interactive ? 3 : 1;
    for (let i = 0; i < count; i++) {
      this.particleService.schedule(i * 1.5 + Math.random() * 2, () =>
        this.particleService.spawnParticle(container, color)
      );
    }
  }

  /**
   * Active les effets visuels spécifiques à la rareté de la carte.
   * Doit être appelée après que les éléments conditionnels (@if) sont montés.
   */
  private initRarityEffects(): void {
    // ── Paillettes montantes (collector + legendary) ──────────────────────
    if (this.hasGlitterEffect && this.particleFromBottomRef) {
      const container = this.particleFromBottomRef.nativeElement;
      const COLORS    = ['#facc15', '#fb923c', '#f472b6', '#fde047', '#fdba74', '#fbbf24'];

      // Particules colorées supplémentaires — réduites en mode statique (carte agrandie)
      const glitterCount = this.interactive ? 12 : 4;
      for (let i = 0; i < glitterCount; i++) {
        const color = COLORS[i % COLORS.length];
        this.particleService.schedule(i * 0.4 + Math.random() * 1.5, () =>
          this.particleService.spawnParticle(container, color)
        );
      }

      // Paillettes carrées lumineuses
      const squareCount = this.interactive ? 6 : 2;
      for (let i = 0; i < squareCount; i++) {
        this.particleService.schedule(i * 0.3 + Math.random() * 0.8, () =>
          this.particleService.spawnGlitter(container)
        );
      }
    }

    // ── Étincelles color-dodge (epic + legendary + collector) ──────────────
    if (this.hasSparkleEffect && this.randomLightLayerRef) {
      const container = this.randomLightLayerRef.nativeElement;
      const sparkleCount = this.interactive ? 18 : 6;
      for (let i = 0; i < sparkleCount; i++) {
        this.particleService.schedule(i * 0.15 + Math.random() * 0.4, () =>
          this.particleService.spawnSparkle(container)
        );
      }
    }

    // ── Foudre canvas (collector) ─────────────────────────────────────────
    if (this.hasLightningEffect && this.lightningCanvasRef) {
      const canvas    = this.lightningCanvasRef.nativeElement;
      canvas.width    = 280;
      canvas.height   = 410;
      this.lightningEffect = new CardLightningEffect(canvas);
      this.startLightningLoop();
    }
  }

  /**
   * Démarre la boucle requestAnimationFrame pour l'effet de foudre.
   * S'exécute en dehors de la zone Angular pour éviter des cycles de
   * détection de changements inutiles.
   */
  private startLightningLoop(): void {
    this.ngZone.runOutsideAngular(() => {
      const loop = (now: number) => {
        const delta = Math.min((now - this.lastFrameTime) / 1000, 0.1);
        this.lastFrameTime = now;
        // Actif seulement quand la face recto est visible
        this.lightningEffect?.update(delta, !this.isFlipped);
        this.animFrameId = requestAnimationFrame(loop);
      };
      this.lastFrameTime = performance.now();
      this.animFrameId   = requestAnimationFrame(loop);
    });
  }

  // ── Interactions souris ───────────────────────────────────────────────────

  /** Lie les événements souris sur la scène racine. */
  private bindMouseEvents(): void {
    const scene = this.sceneRef.nativeElement;
    scene.addEventListener('click', this.onClick);
    // Tilt/parallaxe + lift au survol : coûteux à recomposer une fois la carte
    // agrandie (surface à repeindre proportionnelle au carré du scale), inutile
    // pour un affichage statique.
    if (!this.interactive) return;
    scene.addEventListener('mousemove', this.onMouseMove, { passive: true } as EventListenerOptions);
    scene.addEventListener('mouseover', this.onMouseOver, { passive: true } as EventListenerOptions);
    scene.addEventListener('mouseout',  this.onMouseOut,  { passive: true } as EventListenerOptions);
  }

  /**
   * Retourne la carte côté dos → recto (pas dans l'autre sens).
   * Le re-retournement (recto → dos) n'est pas prévu intentionnellement.
   */
  private readonly onClick = (): void => {
    if (this.isAnimating || !this.isFlipped) return;

    this.soundService.playRarityReveal(this.rarity);
    this.isAnimating = true;
    this.sceneRef.nativeElement.classList.remove('card-face-down');

    gsap.killTweensOf(this.cardWrapRef.nativeElement);
    gsap.to(this.cardWrapRef.nativeElement, {
      rotationY: 0,
      rotationX: 0,
      scale: 1,
      duration: 0.5,
      ease: 'power2.inOut',
      onComplete: () => {
        this.isFlipped   = false;
        this.isAnimating = false;
        this.frontVisible = true;
        this.syncEffectsState();
      },
    });
  };

  /**
   * Mémorise la position souris et planifie UNE application par frame : pas de
   * lecture de layout ni d'écriture de style à chaque événement `mousemove`.
   */
  private readonly onMouseMove = (e: MouseEvent): void => {
    if (this.isFlipped || this.isAnimating) return;
    this.pointerX = e.clientX;
    this.pointerY = e.clientY;
    if (!this.pointerRaf) {
      this.pointerRaf = requestAnimationFrame(this.applyPointer);
    }
  };

  /** Tilt 3D + parallaxe, au plus une fois par frame (1 seule lecture de rect, avant toute écriture). */
  private readonly applyPointer = (): void => {
    this.pointerRaf = 0;
    if (this.isFlipped || this.isAnimating) return;
    this.ensureGsapState();

    const rect = this.sceneRef.nativeElement.getBoundingClientRect();
    const x    = (this.pointerX - rect.left) / rect.width  - 0.5;
    const y    = (this.pointerY - rect.top)  / rect.height - 0.5;

    this.setRotX(y * -24);
    this.setRotY(x * 24);
    this.setScale(1.04);
    this.setParX(x * 14);
    this.setParY(y * 14);
  };

  /**
   * Lève légèrement la carte quand la souris entre dessus (état dos).
   */
  private readonly onMouseOver = (e: MouseEvent): void => {
    if (this.sceneRef.nativeElement.contains(e.relatedTarget as Node)) return;
    if (this.isFlipped) {
      gsap.to(this.cardWrapRef.nativeElement, { y: -14, duration: 0.3, ease: 'power2.out' });
    }
  };

  /**
   * Remet la carte à sa position neutre quand la souris quitte.
   */
  private readonly onMouseOut = (e: MouseEvent): void => {
    if (this.sceneRef.nativeElement.contains(e.relatedTarget as Node)) return;
    if (this.isAnimating) return;

    if (this.pointerRaf) {
      cancelAnimationFrame(this.pointerRaf);
      this.pointerRaf = 0;
    }

    if (this.isFlipped) {
      gsap.to(this.cardWrapRef.nativeElement, { y: 0, duration: 0.3, ease: 'power3.out' });
    } else {
      gsap.to(this.cardWrapRef.nativeElement, {
        rotationX: 0, rotationY: 0, scale: 1,
        duration: 0.55, ease: 'power3.out',
      });
      if (this.gsapStateReady) {
        this.setParX(0);
        this.setParY(0);
      }
    }
  };
}
